import {
  dbui_v1_OperationalAlertList,
  dbuiV1GetOperationalAlertsInfiniteOptions,
  dbuiV1GetOperationalAlertsInfiniteQueryKey,
} from '@antimatterhq/asl-api-ts-v1';
import {
  InfiniteData,
  useInfiniteQuery,
} from '@antimatterhq/ui/hooks/queries/useInfiniteQuery';
import { useQueryClient } from '@antimatterhq/ui/hooks/queries/useQueryClient';
import { VIRTUALIZED_TABLE_DEFAULT_PAGE_SIZE } from '@antimatterhq/ui/constants/table';
import { produce } from 'immer';
import { useWarehouseLocation } from '@/contexts/WarehouseLocationContext';
import { defaultOptions } from '@/utils/query';
import { useWorkspace } from '@/contexts/WorkspaceContext';

export const useGetAlertsInfinite = ({
  includeNotOpen = true,
  beforeDate = undefined,
}: {
  includeNotOpen?: boolean;
  beforeDate?: string;
}) => {
  const { workspace } = useWorkspace();
  const { selectedWarehouse } = useWarehouseLocation();
  const warehouse = selectedWarehouse?.id || '';

  const options = dbuiV1GetOperationalAlertsInfiniteOptions({
    ...defaultOptions(),
    path: { workspace },
    query: {
      warehouse,
      limit: VIRTUALIZED_TABLE_DEFAULT_PAGE_SIZE,
      includeNotOpen,
      beforeDate,
    },
  });

  return useInfiniteQuery({
    ...options,
    enabled: !!workspace && !!warehouse,
    getNextPageParam: (page) => page.cursor,
  });
};

export const useOptimisticUpdateAlertsInfinite = () => {
  const { workspace } = useWorkspace();
  const { selectedWarehouse } = useWarehouseLocation();
  const warehouse = selectedWarehouse?.id || '';
  const queryClient = useQueryClient();

  const optimisticUpdate = (
    updateFn: (
      prev: InfiniteData<dbui_v1_OperationalAlertList, unknown> | undefined,
    ) => InfiniteData<dbui_v1_OperationalAlertList, unknown> | undefined,
    queryParams: {
      includeNotOpen?: boolean;
      beforeDate?: string;
    },
  ) => {
    const { includeNotOpen = true, beforeDate = undefined } = queryParams;

    queryClient.setQueryData(
      dbuiV1GetOperationalAlertsInfiniteQueryKey({
        ...defaultOptions(),
        path: { workspace },
        query: {
          warehouse,
          includeNotOpen,
          beforeDate,
          limit: VIRTUALIZED_TABLE_DEFAULT_PAGE_SIZE,
        },
      }),
      updateFn,
    );
  };

  return optimisticUpdate;
};

export const setDismissAlertQueryData = (
  prev: InfiniteData<dbui_v1_OperationalAlertList, unknown> | undefined,
  idToDismiss: string | string[],
) => {
  if (!prev) {
    console.warn('No data found. Skipping optimistic update');
    return prev;
  }

  return produce(prev, (draft) => {
    draft?.pages.forEach((draftPage) => {
      draftPage.alerts.forEach((alert) => {
        if (Array.isArray(idToDismiss)) {
          if (alert.id && idToDismiss.includes(alert.id)) {
            // eslint-disable-next-line no-param-reassign
            alert.state = 'dismissed';
          }
        } else if (alert.id === idToDismiss) {
          // eslint-disable-next-line no-param-reassign
          alert.state = 'dismissed';
        }
      });
    });
  });
};
